import { imageSize, type RenderImage } from "./canvas-surface";
import { activePage } from "../native/pages";
import { workspaceGridLayout } from "../native/workspace-grid";
import { bounds } from "../native/geometry";
import { effectBounds } from "./native-effects-raster";
import { hasEffects } from "../native/effects";
import { NativeCanvasRenderer } from "./native-canvas";
import { NativeRasterCache } from "./native-raster-cache";
import { NativeVectorDisplay, vectorTile } from "./native-vector-display";
import { visibleNodes } from "./native-visible";
import { roundedPolygonTriangles } from "../native/polygon-corners";
import { cornerRadius } from "../native/corners";
import { multiply, nodeMatrix } from "../native/transforms";
import type { AffineMatrix } from "../geometry/affine";
import type { NativeFrame, NativeRenderer } from "./native-port";
import type { ImageStore } from "./image-store";
import {
  polygonKey,
  polygonParameters,
  polygonTriangles,
} from "../native/shapes";
import type { SceneNode } from "../scene/model";
import { hasPaint, shapeStyle } from "../native/appearance";
import { NativeStrokeRenderer, type GLMesh } from "./native-stroke";

const vertex = `#version 300 es
in vec2 corner;
uniform mat3 objectMatrix;
uniform vec3 camera;
uniform vec2 viewport;
out vec2 uv;
void main() {
  vec2 world = (objectMatrix * vec3(corner - vec2(0.5), 1.0)).xy;
  vec2 pixel = world * camera.z + camera.xy;
  gl_Position = vec4(pixel / viewport * vec2(2.0, -2.0) + vec2(-1.0, 1.0), 0.0, 1.0);
  uv = corner;
}`;
const fragment = `#version 300 es
precision highp float;
in vec2 uv;
uniform vec4 color;
uniform bool textured;
uniform bool ellipse;
uniform vec3 roundedBox;
uniform sampler2D picture;
out vec4 outputColor;
void main() {
  float distance = length((uv - vec2(0.5)) * 2.0);
  float coverage = ellipse ? 1.0 - smoothstep(1.0 - fwidth(distance), 1.0, distance) : 1.0;
  if (roundedBox.z > 0.0) {
    vec2 q = abs((uv - vec2(0.5)) * roundedBox.xy) - roundedBox.xy * 0.5 + roundedBox.z;
    float edge = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0) - roundedBox.z;
    coverage *= 1.0 - smoothstep(-fwidth(edge), 0.0, edge);
  }
  outputColor = color * (textured ? texture(picture, uv) : vec4(1.0));
  outputColor.a *= coverage;
}`;

/** Replaceable GPU backend. Vector meshes contain values, never editor state. */
export class NativeWebGLRenderer implements NativeRenderer {
  readonly kind = "webgl2";
  private blendCanvas = document.createElement("canvas");
  private blendRenderer: NativeCanvasRenderer;
  private raster: NativeRasterCache;
  private vectors: NativeVectorDisplay;
  private usedRaster = new Set<RenderImage>();
  private usedTextures = new Set<RenderImage>();
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private buffer: WebGLBuffer;
  private workspaceGridBuffer: WebGLBuffer;
  private corner: number;
  private strokes: NativeStrokeRenderer;
  private meshes = new Map<string, { buffer: WebGLBuffer; count: number }>();
  private usedMeshes = new Set<string>();
  private pixelsPerUnit = 1;
  private textures = new Map<RenderImage, WebGLTexture>();
  private white: WebGLTexture;
  private uniforms: Record<string, WebGLUniformLocation> = {};
  constructor(
    private canvas: HTMLCanvasElement,
    private images: ImageStore,
  ) {
    this.raster = new NativeRasterCache(images, true);
    this.vectors = new NativeVectorDisplay(images.pixels);
    this.blendRenderer = new NativeCanvasRenderer(
      this.blendCanvas,
      images,
      true,
      (document, node, density) => this.raster.get(document, node, density),
    );
    const gl = canvas.getContext("webgl2", {
      alpha: false,
      premultipliedAlpha: false,
      antialias: true,
      stencil: true,
    });
    if (!gl) throw new Error("WebGL2 недоступен");
    if (!gl.getContextAttributes()?.stencil)
      throw new Error("GPU-обводка недоступна");
    this.gl = gl;
    this.strokes = new NativeStrokeRenderer(gl);
    const shaders = [
      this.shader(gl.VERTEX_SHADER, vertex),
      this.shader(gl.FRAGMENT_SHADER, fragment),
    ];
    const program = gl.createProgram()!;
    for (const shader of shaders) gl.attachShader(program, shader);
    gl.linkProgram(program);
    for (const shader of shaders) gl.deleteShader(shader);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS))
      throw new Error("Не удалось запустить GPU-отрисовку");
    this.program = program;
    gl.useProgram(program);
    for (const name of [
      "objectMatrix",
      "camera",
      "viewport",
      "color",
      "textured",
      "ellipse",
      "roundedBox",
      "picture",
    ]) {
      const location = gl.getUniformLocation(program, name);
      if (!location) throw new Error("Не удалось связать GPU-программу");
      this.uniforms[name] = location;
    }
    this.buffer = gl.createBuffer()!;
    this.workspaceGridBuffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]),
      gl.STATIC_DRAW,
    );
    const corner = gl.getAttribLocation(program, "corner");
    this.corner = corner;
    gl.enableVertexAttribArray(corner);
    gl.vertexAttribPointer(corner, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(
      gl.SRC_ALPHA,
      gl.ONE_MINUS_SRC_ALPHA,
      gl.ONE,
      gl.ONE_MINUS_SRC_ALPHA,
    );
    this.white = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.white);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      1,
      1,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      new Uint8Array([255, 255, 255, 255]),
    );
    this.textureParameters();
    gl.uniform1i(this.uniforms.picture!, 0);
  }
  private shader(type: number, source: string) {
    const gl = this.gl,
      shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      throw new Error("Не удалось собрать GPU-программу");
    }
    return shader;
  }
  private textureParameters() {
    const gl = this.gl;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }
  private texture(image: RenderImage) {
    this.usedTextures.add(image);
    const cached = this.textures.get(image);
    if (cached) return cached;
    const gl = this.gl,
      texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    let source: TexImageSource = image;
    const iw = imageSize(image).width,
      ih = imageSize(image).height;
    if (iw > max || ih > max) {
      const preview = document.createElement("canvas"),
        ratio = Math.min(max / iw, max / ih);
      preview.width = Math.max(1, Math.floor(iw * ratio));
      preview.height = Math.max(1, Math.floor(ih * ratio));
      preview
        .getContext("2d")!
        .drawImage(image, 0, 0, preview.width, preview.height);
      source = preview;
    }
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    this.textureParameters();
    this.textures.set(image, texture);
    return texture;
  }
  private rectangle(
    matrix: AffineMatrix,
    fill: string,
    opacity: number,
    image?: RenderImage,
    node?: SceneNode,
    meshOverride?: GLMesh,
  ) {
    const gl = this.gl,
      value = parseInt(fill.slice(1), 16);
    gl.uniformMatrix3fv(this.uniforms.objectMatrix!, false, [
      matrix[0],
      matrix[1],
      0,
      matrix[2],
      matrix[3],
      0,
      matrix[4],
      matrix[5],
      1,
    ]);
    gl.uniform4f(
      this.uniforms.color!,
      ((value >> 16) & 255) / 255,
      ((value >> 8) & 255) / 255,
      (value & 255) / 255,
      opacity,
    );
    gl.uniform1i(this.uniforms.textured!, image ? 1 : 0);
    gl.uniform1i(this.uniforms.ellipse!, node?.kind === "ellipse" ? 1 : 0);
    gl.uniform3f(
      this.uniforms.roundedBox!,
      node?.geometry.width ?? 1,
      node?.geometry.height ?? 1,
      node && node.kind !== "polygon" ? cornerRadius(node) : 0,
    );
    gl.bindTexture(gl.TEXTURE_2D, image ? this.texture(image) : this.white);
    let mesh = { buffer: this.buffer, count: 6 };
    if (node?.kind === "polygon") {
      const rounded = cornerRadius(node) > 0;
      const stretch = Math.hypot(
        matrix[0] / node.geometry.width!,
        matrix[1] / node.geometry.width!,
        matrix[2] / node.geometry.height!,
        matrix[3] / node.geometry.height!,
      );
      const tolerance = 0.15 / Math.max(stretch * this.pixelsPerUnit, 1e-8);
      const key = rounded
        ? JSON.stringify([node.geometry, tolerance])
        : polygonKey(node);
      this.usedMeshes.add(key);
      let cached = this.meshes.get(key);
      if (!cached) {
        const vertices = rounded
          ? roundedPolygonTriangles(node, tolerance)
          : polygonTriangles(polygonParameters(node));
        cached = { buffer: gl.createBuffer()!, count: vertices.length / 2 };
        gl.bindBuffer(gl.ARRAY_BUFFER, cached.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
        this.meshes.set(key, cached);
      }
      mesh = cached;
    }
    mesh = meshOverride ?? mesh;
    gl.bindBuffer(gl.ARRAY_BUFFER, mesh.buffer);
    gl.vertexAttribPointer(this.corner, 2, gl.FLOAT, false, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, mesh.count);
  }
  draw({ document, camera, width, height, dpr, workspaceGrid }: NativeFrame) {
    if (!activePage(document).nodes.length) this.vectors.dispose();
    this.raster.beginFrame({ document, camera, width, height, dpr });
    this.vectors.begin(visibleNodes({ document, camera, width, height, dpr }));
    const gl = this.gl;
    this.pixelsPerUnit = camera.zoom * dpr;
    this.usedMeshes.clear();
    if (this.canvas.width !== Math.round(width * dpr))
      this.canvas.width = Math.round(width * dpr);
    if (this.canvas.height !== Math.round(height * dpr))
      this.canvas.height = Math.round(height * dpr);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    const workspace = parseInt(
      activePage(document).workspaceColor.slice(1),
      16,
    );
    gl.clearColor(
      ((workspace >> 16) & 255) / 255,
      ((workspace >> 8) & 255) / 255,
      (workspace & 255) / 255,
      1,
    );
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.program);
    gl.uniform3f(this.uniforms.camera!, camera.x, camera.y, camera.zoom);
    gl.uniform2f(this.uniforms.viewport!, width, height);
    const page = activePage(document);
    this.usedTextures.clear();
    if (
      page.nodes.some(
        (n) =>
          n.appearance.blendMode && n.appearance.blendMode !== "source-over",
      )
    ) {
      this.blendRenderer.draw({
        document,
        camera,
        width,
        height,
        dpr,
        workspaceGrid,
      });
      const old = this.textures.get(this.blendCanvas);
      if (old) gl.deleteTexture(old);
      this.textures.delete(this.blendCanvas);
      gl.uniform3f(this.uniforms.camera!, 0, 0, 1);
      this.rectangle(
        [width, 0, 0, height, width / 2, height / 2],
        "#ffffff",
        1,
        this.blendCanvas,
      );
      this.pruneTextures();
      this.raster.prune(new Set(page.nodes.map((n) => n.id)));
      return;
    }

    const grid = workspaceGridLayout(
      camera,
      page.size,
      width,
      height,
      dpr,
      workspaceGrid,
      page.workspaceColor,
    );
    if (grid.rectangles.length) {
      const vertices = new Float32Array(
        grid.rectangles.flatMap(({ x, y, width: w, height: h }) => [
          x,
          y,
          x + w,
          y,
          x,
          y + h,
          x,
          y + h,
          x + w,
          y,
          x + w,
          y + h,
        ]),
      );
      gl.bindBuffer(gl.ARRAY_BUFFER, this.workspaceGridBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
      gl.uniform3f(this.uniforms.camera!, 0, 0, 1);
      this.rectangle(
        [1, 0, 0, 1, 0.5, 0.5],
        grid.color,
        1,
        undefined,
        undefined,
        { buffer: this.workspaceGridBuffer, count: vertices.length / 2 },
      );
      gl.uniform3f(this.uniforms.camera!, camera.x, camera.y, camera.zoom);
    }
    if (page.backgroundVisible)
      this.rectangle(
        [
          page.size.width,
          0,
          0,
          page.size.height,
          page.size.width / 2,
          page.size.height / 2,
        ],
        "#ffffff",
        1,
      );
    const sources = new Map(document.assets.map((a) => [a.id, a.source]));
    this.usedRaster.clear();
    const visibleIds = new Set<string>();
    this.strokes.begin();
    for (const node of page.nodes) {
      if (node.appearance.visible === false || !hasPaint(node)) continue;
      const box = hasEffects(node) ? effectBounds(node) : bounds(node);
      const pad = Math.max(16, (node.appearance.strokeWidth || 0) * 5);
      if (
        (box.x + box.width + pad) * camera.zoom + camera.x < 0 ||
        (box.y + box.height + pad) * camera.zoom + camera.y < 0 ||
        (box.x - pad) * camera.zoom + camera.x > width ||
        (box.y - pad) * camera.zoom + camera.y > height
      )
        continue;
      visibleIds.add(node.id);
      if (vectorTile(node)) {
        const tile = this.vectors.get(node, camera.zoom * dpr);
        this.rectangle(
          tile.matrix,
          "#ffffff",
          node.appearance.opacity ?? 1,
          tile.canvas,
        );
        continue;
      }
      if (
        isInteractive(node) ||
        hasEffects(node) ||
        node.kind === "text" ||
        node.kind === "path" ||
        node.layer.clipToBelow
      ) {
        const entry = this.raster.get(document, node, camera.zoom * dpr),
          b = entry.box,
          placement: AffineMatrix = [
            b.width,
            0,
            0,
            b.height,
            b.x + b.width / 2,
            b.y + b.height / 2,
          ];
        this.usedRaster.add(entry.canvas);
        this.rectangle(
          entry.transform ? multiply(entry.transform, placement) : placement,
          "#ffffff",
          1,
          entry.canvas,
        );
        continue;
      }
      if (
        this.strokes.draw(
          node,
          nodeMatrix(node),
          camera.zoom * dpr,
          (matrix, color, opacity, shape, mesh) =>
            this.rectangle(matrix, color, opacity, undefined, shape, mesh),
        )
      )
        continue;
      if (node.kind !== "image" && !shapeStyle(node).fillEnabled) continue;
      const matrix = nodeMatrix(node);
      const image =
        node.kind === "image"
          ? this.images.display(
              sources.get(node.media.image!) || "",
              Math.hypot(matrix[0], matrix[1]) * camera.zoom * dpr,
              Math.hypot(matrix[2], matrix[3]) * camera.zoom * dpr,
            )
          : undefined;
      this.rectangle(
        matrix,
        image ? "#ffffff" : String(node.appearance.fill),
        node.appearance.opacity ?? 1,
        image,
        node,
      );
    }
    this.strokes.end();
    this.raster.prune(visibleIds);
    for (const [key, mesh] of this.meshes)
      if (!this.usedMeshes.has(key)) {
        gl.deleteBuffer(mesh.buffer);
        this.meshes.delete(key);
      }
    this.pruneTextures();
  }
  private pruneTextures() {
    for (const [image, texture] of this.textures)
      if (!this.usedTextures.has(image)) {
        this.gl.deleteTexture(texture);
        this.textures.delete(image);
      }
  }
  dispose() {
    this.vectors.dispose();
    this.raster.dispose();
    this.blendRenderer.dispose();
    const gl = this.gl;
    for (const texture of this.textures.values()) gl.deleteTexture(texture);
    gl.deleteTexture(this.white);
    gl.deleteBuffer(this.buffer);
    gl.deleteBuffer(this.workspaceGridBuffer);
    this.strokes.dispose();
    for (const mesh of this.meshes.values()) gl.deleteBuffer(mesh.buffer);
    this.meshes.clear();
    gl.deleteProgram(this.program);
    this.textures.clear();
  }
}
import { isInteractive } from "../native/interactive";
